import { Link } from 'react-router-dom';
import { useT } from '../i18n/index.ts';
import { Icon, cx } from '../ui/index.ts';
import type { NavItem } from './navigation.ts';

/**
 * The nav rows, rendered identically by the persistent sidebar and by
 * the mobile drawer. Both get the same list from `visibleNavItems` and
 * the same `activePath` from `activeNavItemForPath`, so the two can
 * never disagree about which row is lit.
 *
 * Active state is an explicit `aria-current` driven by that resolver,
 * NOT `NavLink`'s own matching: the header title reads from the same
 * value, and two independent matchers is how the title and the
 * highlight drifted apart in the first place.
 *
 * `rail` collapses a row to its glyph. The label stays in the DOM as
 * `sr-only` — a 56px icon column must not cost a screen-reader user the
 * destination name.
 */
interface Props {
  readonly items: readonly NavItem[];
  readonly activePath: string | null;
  /** `shell-nav` in the sidebar, `shell-mobile-nav` in the drawer. */
  readonly testIdPrefix: string;
  readonly rail?: boolean;
  readonly onNavigate?: () => void;
}

export function ShellNavList({
  items,
  activePath,
  testIdPrefix,
  rail = false,
  onNavigate,
}: Props): JSX.Element {
  const t = useT();
  return (
    <>
      {items.map((item) => {
        const isActive = item.to === activePath;
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            aria-current={isActive ? 'page' : undefined}
            title={rail ? t(item.labelKey) : undefined}
            data-testid={`${testIdPrefix}-${item.testId}`}
            className={cx(
              'flex items-center gap-3 rounded px-3 py-2 text-label transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40',
              rail && 'justify-center px-0',
              isActive
                ? 'bg-forest-tint text-forest-deep'
                : 'text-ink-mid hover:bg-active/50 hover:text-ink',
            )}
          >
            <Icon name={item.icon} size="sm" />
            <span className={cx(rail && 'sr-only')}>{t(item.labelKey)}</span>
          </Link>
        );
      })}
    </>
  );
}
